import { Component, ReactNode, Suspense } from 'react';
import { RouteLoadingFallback } from './route-loading-fallback';

const LAZY_ROUTE_CHUNK_RELOAD_STORAGE_KEY = 'wavvest.lazyRouteChunkReloadAttempt';
const lazyRouteChunkReloadAttempts = new Set<string>();
const lazyRouteChunkErrorPatterns = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
];

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`.trim();
  }

  return String(error ?? '');
};

const getChunkReloadMarker = (error: unknown): string => {
  const locationKey =
    typeof window === 'undefined'
      ? 'unknown-route'
      : `${window.location.origin}${window.location.pathname}${window.location.search}`;

  return `${locationKey}|${getErrorText(error)}`;
};

const storageMatchesReloadMarker = (marker: string): boolean => {
  try {
    return window.sessionStorage.getItem(LAZY_ROUTE_CHUNK_RELOAD_STORAGE_KEY) === marker;
  } catch {
    return false;
  }
};

const rememberReloadMarker = (marker: string) => {
  lazyRouteChunkReloadAttempts.add(marker);

  try {
    window.sessionStorage.setItem(LAZY_ROUTE_CHUNK_RELOAD_STORAGE_KEY, marker);
  } catch {
    // The in-memory marker still prevents repeated attempts in this app shell.
  }
};

export const isLazyRouteChunkLoadError = (error: unknown): boolean => {
  const errorText = getErrorText(error);

  return lazyRouteChunkErrorPatterns.some((pattern) => pattern.test(errorText));
};

export const attemptLazyRouteChunkRecovery = (
  error: unknown,
  reloadPage: () => void = () => window.location.reload(),
): boolean => {
  if (typeof window === 'undefined' || !isLazyRouteChunkLoadError(error)) {
    return false;
  }

  const marker = getChunkReloadMarker(error);
  if (lazyRouteChunkReloadAttempts.has(marker) || storageMatchesReloadMarker(marker)) {
    return false;
  }

  rememberReloadMarker(marker);
  reloadPage();
  return true;
};

type LazyRouteErrorBoundaryProps = {
  children: ReactNode;
};

type LazyRouteErrorBoundaryState = {
  error: unknown;
  shouldRethrow: boolean;
};

class LazyRouteErrorBoundary extends Component<
  LazyRouteErrorBoundaryProps,
  LazyRouteErrorBoundaryState
> {
  state: LazyRouteErrorBoundaryState = {
    error: null,
    shouldRethrow: false,
  };

  static getDerivedStateFromError(error: unknown): LazyRouteErrorBoundaryState {
    return {
      error,
      shouldRethrow: !isLazyRouteChunkLoadError(error),
    };
  }

  componentDidCatch(error: unknown) {
    if (!attemptLazyRouteChunkRecovery(error)) {
      this.setState({ shouldRethrow: true });
    }
  }

  render() {
    const { error, shouldRethrow } = this.state;

    if (!error) {
      return this.props.children;
    }

    if (shouldRethrow) {
      throw error;
    }

    return <RouteLoadingFallback />;
  }
}

/**
 * The Suspense boundary shared by lazy advisor portal routes, public auth
 * routes, and protected portal routes alike.
 *
 * Kept in its own module so the fallback has one owner. Login handoffs keep the
 * full-screen loader, while ordinary in-app navigation falls back to skeletons.
 */
export const lazyLoad = (children: ReactNode): ReactNode => (
  <LazyRouteErrorBoundary>
    <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
  </LazyRouteErrorBoundary>
);
